"use client"

import { useState, useEffect, useMemo } from "react"
import { toast } from "sonner"
import axiosInstance from "../../../../libs/axiosInstance"

const PREVIEW_BATCH_SIZE = 50

const QuestionSidebar = ({ topicId, onImportQuestions }) => {
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedQuestions, setSelectedQuestions] = useState(new Set())
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [importCount, setImportCount] = useState(5) // Default to 5 questions
  const [importMode, setImportMode] = useState("selected") // "selected", "first", "random"
  const [randomIndices, setRandomIndices] = useState([]) // Store random indices for preview
  const [visibleCount, setVisibleCount] = useState(PREVIEW_BATCH_SIZE)

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true)
      try {
        const response = await axiosInstance.get(`/question_bank/question-bank/${topicId}/`)
        setQuestions(response.data.questions)
      } catch (err) {
        setError("Failed to fetch questions.")
        toast.error("Failed to fetch questions for this topic.")
      } finally {
        setLoading(false)
      }
    }

    if (topicId) {
      fetchQuestions()
    }
  }, [topicId])

  useEffect(() => {
    setVisibleCount(PREVIEW_BATCH_SIZE)
  }, [topicId])

  // Generate random indices when mode changes to random or count changes
  useEffect(() => {
    if (importMode === "random" && questions.length > 0) {
      const indices = Array.from({ length: questions.length }, (_, i) => i)
      const shuffled = indices.sort(() => Math.random() - 0.5)
      setRandomIndices(shuffled.slice(0, Math.min(importCount, questions.length)))
    }
  }, [importMode, importCount, questions.length])

  const handleQuestionSelect = (questionId, checked) => {
    const newSelected = new Set(selectedQuestions)
    if (checked) {
      newSelected.add(questionId)
    } else {
      newSelected.delete(questionId)
    }
    setSelectedQuestions(newSelected)
  }

  const randomIndexSet = useMemo(() => new Set(randomIndices), [randomIndices])
  const visibleQuestions = useMemo(() => questions.slice(0, visibleCount), [questions, visibleCount])

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedQuestions(new Set(questions.map((q) => q.id)))
    } else {
      setSelectedQuestions(new Set())
    }
  }

  const handleImportSelected = () => {
    let questionsToImport = []

    if (importMode === "selected") {
      questionsToImport = questions.filter((q) => selectedQuestions.has(q.id))
      if (questionsToImport.length === 0) {
        toast.error("Please select questions to import")
        return
      }
    } else if (importMode === "first") {
      questionsToImport = questions.slice(0, Math.min(importCount, questions.length))
    } else if (importMode === "random") {
      // Use the actual random indices for import
      questionsToImport = randomIndices.map((index) => questions[index]).filter(Boolean)
    }

    if (questionsToImport.length === 0) {
      toast.error("No questions to import")
      return
    }

    onImportQuestions(questionsToImport.map((q) => ({ ...q, isNew: true })))
    toast.success(`${questionsToImport.length} question(s) imported successfully`)
    setSelectedQuestions(new Set())
    setIsDrawerOpen(false)
  }

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case "objective":
        return "Multiple Choice"
      case "theory":
        return "Theory"
      case "matching":
        return "Matching"
      case "multiple_select":
        return "Multiple Select"
      default:
        return type
    }
  }

  const getQuestionTypeBadge = (type) => {
    const badges = {
      objective: "badge-primary",
      theory: "badge-success",
      matching: "badge-secondary",
      multiple_select: "badge-warning",
    }
    return badges[type] || "badge-neutral"
  }

  const getImportButtonText = () => {
    switch (importMode) {
      case "selected":
        return `Import Selected (${selectedQuestions.size})`
      case "first":
        return `Import First ${Math.min(importCount, questions.length)}`
      case "random":
        return `Import Random ${Math.min(importCount, questions.length)}`
      default:
        return "Import Questions"
    }
  }

  // Check if a question will be imported based on current mode
  const willBeImported = (index) => {
    if (importMode === "first") {
      return index < importCount
    } else if (importMode === "random") {
      return randomIndexSet.has(index)
    }
    return false
  }

  const SidebarContent = () => (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold mb-1">Questions in Topic</h3>
        <p className="text-sm opacity-70">{questions.length} question(s) available</p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="alert alert-error">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && questions.length === 0 && (
        <div className="text-center py-8">
          <div className="text-6xl opacity-20 mb-4">📝</div>
          <p className="opacity-70">No questions found for this topic.</p>
        </div>
      )}

      {/* Questions List */}
      {questions.length > 0 && (
        <>
          {/* Import Controls Card */}
          <div className="card bg-base-200 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-base font-semibold">Import Questions</h4>
                <div className="badge badge-neutral">
                  {importMode === "selected" ? `${selectedQuestions.size} selected` : `${questions.length} available`}
                </div>
              </div>
              <p className="text-sm opacity-70 mb-4">Choose how many questions to import into your quiz</p>

              {/* Import Mode Selection - Compact */}
              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text font-medium">Import Mode</span>
                </label>
                <div className="grid grid-cols-1 gap-1">
                  <div
                    className={`card cursor-pointer transition-all duration-200 ${
                      importMode === "selected"
                        ? "bg-primary text-primary-content border border-primary"
                        : "bg-base-100 hover:bg-base-200 border border-base-300"
                    }`}
                    onClick={() => setImportMode("selected")}
                  >
                    <div className="card-body p-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full flex items-center justify-center ${
                            importMode === "selected" ? "bg-primary-content text-primary" : "bg-base-300"
                          }`}
                        >
                          {importMode === "selected" && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-2 w-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <h5 className="text-sm font-medium">Selected</h5>
                          <p className="text-xs opacity-70">Choose specific questions</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`card cursor-pointer transition-all duration-200 ${
                      importMode === "first"
                        ? "bg-primary text-primary-content border border-primary"
                        : "bg-base-100 hover:bg-base-200 border border-base-300"
                    }`}
                    onClick={() => setImportMode("first")}
                  >
                    <div className="card-body p-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full flex items-center justify-center ${
                            importMode === "first" ? "bg-primary-content text-primary" : "bg-base-300"
                          }`}
                        >
                          {importMode === "first" && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-2 w-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <h5 className="text-sm font-medium">First N</h5>
                          <p className="text-xs opacity-70">First questions in order</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`card cursor-pointer transition-all duration-200 ${
                      importMode === "random"
                        ? "bg-primary text-primary-content border border-primary"
                        : "bg-base-100 hover:bg-base-200 border border-base-300"
                    }`}
                    onClick={() => setImportMode("random")}
                  >
                    <div className="card-body p-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full flex items-center justify-center ${
                            importMode === "random" ? "bg-primary-content text-primary" : "bg-base-300"
                          }`}
                        >
                          {importMode === "random" && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-2 w-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <h5 className="text-sm font-medium">Random N</h5>
                          <p className="text-xs opacity-70">Randomly selected questions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Number Input for First/Random modes */}
              {(importMode === "first" || importMode === "random") && (
                <div className="form-control mb-4">
                  <label className="label">
                    <span className="label-text font-medium">Number of Questions</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      className="input input-bordered input-sm flex-1"
                      value={importCount}
                      onChange={(e) =>
                        setImportCount(Math.max(1, Math.min(questions.length, Number.parseInt(e.target.value) || 1)))
                      }
                      min="1"
                      max={questions.length}
                    />
                    <span className="text-sm opacity-70">/ {questions.length}</span>
                  </div>
                  <div className="label">
                    <span className="label-text-alt opacity-70">
                      {importMode === "first"
                        ? "Import the first questions in order"
                        : "Import randomly selected questions"}
                    </span>
                  </div>
                </div>
              )}

              {/* Quick Number Buttons for First/Random modes */}
              {(importMode === "first" || importMode === "random") && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {[5, 10, 15, 20]
                    .filter((num) => num <= questions.length)
                    .map((num) => (
                      <button
                        key={num}
                        className={`btn btn-xs ${importCount === num ? "btn-primary" : "btn-outline"}`}
                        onClick={() => setImportCount(num)}
                      >
                        {num}
                      </button>
                    ))}
                  {questions.length > 20 && (
                    <button
                      className={`btn btn-xs ${importCount === questions.length ? "btn-primary" : "btn-outline"}`}
                      onClick={() => setImportCount(questions.length)}
                    >
                      All ({questions.length})
                    </button>
                  )}
                </div>
              )}

              {/* Select All for Selected mode */}
              {importMode === "selected" && (
                <div className="form-control mb-4">
                  <label className="label cursor-pointer justify-start gap-3 py-2">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={selectedQuestions.size === questions.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                    <span className="label-text font-medium">Select all questions</span>
                  </label>
                </div>
              )}

              {/* Import Button */}
              <button
                className="btn btn-primary btn-sm w-full"
                onClick={handleImportSelected}
                disabled={importMode === "selected" && selectedQuestions.size === 0}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {getImportButtonText()}
              </button>
            </div>
          </div>

          {/* Separator */}
          <div className="divider my-2"></div>

          {/* Questions Preview */}
          {importMode === "selected" && (
            <div className="text-sm opacity-70 mb-2">
              <span className="font-medium">Select questions below to import:</span>
            </div>
          )}

          {(importMode === "first" || importMode === "random") && (
            <div className="alert alert-info alert-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                className="stroke-current shrink-0 w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <span className="text-xs">
                {importMode === "first"
                  ? `Will import the first ${Math.min(importCount, questions.length)} questions`
                  : `Will import ${Math.min(importCount, questions.length)} randomly selected questions`}
              </span>
            </div>
          )}

          {/* Scrollable Questions List */}
          <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
            {visibleQuestions.map((question, index) => (
              <div key={question.id} className="card bg-base-100 shadow-sm hover:shadow-md transition-all duration-200">
                <div className="card-body p-4">
                  <div className="flex items-start gap-3">
                    {importMode === "selected" && (
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm mt-1 flex-shrink-0"
                        checked={selectedQuestions.has(question.id)}
                        onChange={(e) => handleQuestionSelect(question.id, e.target.checked)}
                      />
                    )}

                    {importMode !== "selected" && (
                      <div className="flex-shrink-0 mt-1">
                        <div
                          className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
                            willBeImported(index)
                              ? "bg-primary text-primary-content"
                              : "bg-base-300 text-base-content opacity-50"
                          }`}
                        >
                          {index + 1}
                        </div>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      {/* Question Header */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <div className={`badge badge-sm ${getQuestionTypeBadge(question.question_type)}`}>
                          {getQuestionTypeLabel(question.question_type)}
                        </div>
                        <span className="text-xs opacity-60">#{index + 1}</span>
                        {importMode === "selected" && selectedQuestions.has(question.id) && (
                          <div className="badge badge-sm badge-primary">Selected</div>
                        )}
                        {importMode !== "selected" && willBeImported(index) && (
                          <div className="badge badge-sm badge-success">Will Import</div>
                        )}
                      </div>

                      {/* Question Text */}
                      <p className="text-sm font-medium mb-2 leading-relaxed">
                        {question.text.slice(0, 100)}
                        {question.text.length > 100 ? "..." : ""}
                      </p>

                      {/* Question Details - Collapsible */}
                      {question.question_type === "objective" && question.options && (
                        <div className="collapse collapse-arrow bg-base-200 collapse-sm">
                          <input type="checkbox" />
                          <div className="collapse-title text-xs py-2 min-h-0 font-medium">
                            View {question.options.length} options
                          </div>
                          <div className="collapse-content">
                            <div className="space-y-1 pt-1">
                              {question.options.slice(0, 3).map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center text-xs gap-2">
                                  <div
                                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                      option.is_correct ? "bg-success" : "bg-base-300"
                                    }`}
                                  ></div>
                                  <span className="truncate">{option.text}</span>
                                </div>
                              ))}
                              {question.options.length > 3 && (
                                <p className="text-xs opacity-60 pl-4">+{question.options.length - 3} more options</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {question.question_type === "matching" && question.matching_pair && (
                        <div className="text-xs opacity-60 bg-base-200 px-2 py-1 rounded">
                          {question.matching_pair.length} matching pairs
                        </div>
                      )}

                      {question.question_type === "theory" && question.answer && (
                        <div className="text-xs opacity-60 bg-base-200 px-2 py-1 rounded">
                          Answer: {question.answer.slice(0, 50)}
                          {question.answer.length > 50 ? "..." : ""}
                        </div>
                      )}

                      {question.question_type === "multiple_select" && question.options && (
                        <div className="text-xs opacity-60 bg-base-200 px-2 py-1 rounded">
                          {question.options.filter((opt) => opt.is_correct).length} correct out of{" "}
                          {question.options.length} options
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {questions.length > visibleQuestions.length && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={() => setVisibleCount((prev) => prev + PREVIEW_BATCH_SIZE)}
                >
                  Show more ({questions.length - visibleQuestions.length} remaining)
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-80 sticky top-4">
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body p-6">
            <SidebarContent />
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <div className="lg:hidden">
        <button className="btn btn-outline btn-sm mb-4" onClick={() => setIsDrawerOpen(true)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          Import Questions
          {questions.length > 0 && <div className="badge badge-neutral ml-2">{questions.length}</div>}
        </button>

        {/* Drawer Implementation */}
        <div className="drawer drawer-end">
          <input
            id="question-drawer"
            type="checkbox"
            className="drawer-toggle"
            checked={isDrawerOpen}
            onChange={(e) => setIsDrawerOpen(e.target.checked)}
          />
          <div className="drawer-side z-50">
            <label htmlFor="question-drawer" className="drawer-overlay" onClick={() => setIsDrawerOpen(false)}></label>
            <div className="w-80 min-h-full bg-base-100 flex flex-col">
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-4 border-b border-base-300">
                <div>
                  <h3 className="text-lg font-semibold">Import Questions</h3>
                  <p className="text-sm opacity-70">Select questions from this topic to import into your quiz</p>
                </div>
                <button className="btn btn-sm btn-circle btn-ghost" onClick={() => setIsDrawerOpen(false)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-1 p-4 overflow-y-auto">
                <SidebarContent />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default QuestionSidebar